/* eslint-disable react/prop-types */
import axios from 'axios';
import Swal from 'sweetalert2';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// api
import ApiKey from '../../api/ApiKey';
import BaseApi from '../../api/BaseApi';
// components
import SmallLoading from '../../element/SmallLoading';
// helpers
import { logOutIfTokenExpired, resultInfo } from '../../helpers';
// mui
import Tooltip from '@mui/material/Tooltip';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
// import VisibilityIcon from '@mui/icons-material/Visibility';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

const ShowVacancies = ({
  t,
  tokenKey,
  managerId,
  searchQuery,
  vacanciesCount,
}) => {
  const navigate = useNavigate();

  const [sort, setSort] = useState('date');
  const [vacancies, setVacancies] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [smallLoading, setSmallLoading] = useState(true);
  const [showVacancies, setShowVacancies] = useState(false);

  const handleSortChange = sortBy => { // TODO: Update this function after backend changes
    setPagination(prev => ({
      ...prev,
      current_page: 1,
    }));

    setSort(sortBy);
    getData(sortBy);
  };

  const handleShowVacancies = () => {
    setShowVacancies(true);
    getData();
  };

  const handlePageChange = async page => {
    try {
      setSmallLoading(true);
      setPagination(prev => ({
        ...prev,
        current_page: page,
      }));

      const response = await axios.get(
        BaseApi + `/job/management/${managerId}`,
        {
          params: {
            page,
            sort,
            search: searchQuery ? searchQuery : null,
            per_page: pagination ? pagination.per_page : 10,
          },
          headers: {
            key: ApiKey,
            token: tokenKey,
            contentType: 'application/json',
          },
        },
      );

      const element = document.getElementById(managerId);

      if (element) {
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offset = 90;
        window.scrollTo({
          top: elementPosition - offset,
          behavior: 'smooth',
        });
      }

      if (response.data.status === 200) {
        setVacancies(response.data.data);
        setPagination(response.data.meta);
      } else {
        resultInfo('error', response.data.message, t('employerManageJobs.close'));
      }

      setSmallLoading(false);
    } catch (error) {
      const element = document.getElementById(managerId);

      if (element) {
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offset = 90;
        window.scrollTo({
          top: elementPosition - offset,
          behavior: 'smooth',
        });
      }

      setSmallLoading(false);

      console.error('Error fetching Jobs List', error);
    }
  };

  const getData = async (sortBy = null) => {
    try {
      setSmallLoading(true);

      const response = await axios.post(
        BaseApi + `/job/management/${managerId}`,
        null,
        {
          params: {
            sort: sortBy,
            search: searchQuery ? searchQuery : null,
            page: pagination ? pagination.current_page : 1,
            per_page: pagination ? pagination.per_page : 10,
          },
          headers: {
            key: ApiKey,
            token: tokenKey,
            contentType: 'application/json',
          },
        },
      );

      if (response.data.status === 200) {
        setVacancies(response.data.data);
        setPagination(response.data.meta);
      } else {
        resultInfo('error', response.data.message, t('employerManageJobs.close'));
      }

      setSmallLoading(false);
    } catch (error) {
      setSmallLoading(false);

      if (error.message === 'Network Error' || error.response.data.message === 'Token has expired') {
        navigate('/');

        logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
      }

      console.error('Cannot get manage job data', error);
    }
  };

  const handleActivate = async slug => {
    try {
      const confirmationResult = await Swal.fire({
        icon: 'question',
        showCancelButton: true,
        cancelButtonText: t('employerManageJobs.no'),
        confirmButtonText: t('employerManageJobs.yes'),
        text: t('employerManageJobs.activateConfirmTxt'),
        title: t('employerManageJobs.activateConfirmTitle'),
      });

      if (confirmationResult.isConfirmed) {
        const response = await axios.post(
          BaseApi + `/job/activate/${slug}`,
          null,
          {
            headers: {
              key: ApiKey,
              token: tokenKey,
              'Content-Type': 'application/json',
            },
          },
        );

        if (response.data.status === 200) {
          resultInfo('success', t('employerManageJobs.activateSuccessTitle'), t('membershipPlan.close'));
        } else {
          resultInfo('error', response.data.message, t('membershipPlan.close'));
        }

        getData();
      }
    } catch (error) {
      if (error.message === 'Network Error' || error.response.data.message === 'Token has expired') {
        navigate('/');

        logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
      } else {
        resultInfo('error', t('employerManageJobs.activateFailedTitle'), t('membershipPlan.close'));
      }

      console.error('Error activating job:', error);
    }
  };

  const handleDeactivate = async slug => {
    try {
      const confirmationResult = await Swal.fire({
        icon: 'question',
        showCancelButton: true,
        cancelButtonText: t('employerManageJobs.no'),
        confirmButtonText: t('employerManageJobs.yes'),
        text: t('employerManageJobs.deactivateConfirmTxt'),
        title: t('employerManageJobs.deactivateConfirmTitle'),
      });

      if (confirmationResult.isConfirmed) {
        const response = await axios.post(
          BaseApi + `/job/deactivate/${slug}`,
          null,
          {
            headers: {
              key: ApiKey,
              token: tokenKey,
              'Content-Type': 'application/json',
            },
          },
        );

        if (response.data.status === 200) {
          resultInfo('success', t('employerManageJobs.deactivateSuccessTitle'), t('employerManageJobs.close'));
        } else {
          resultInfo('error', response.data.message, t('employerManageJobs.close'));
        }

        getData();
      }
    } catch (error) {
      if (error.message === 'Network Error' || error.response.data.message === 'Token has expired') {
        navigate('/');

        logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
      } else {
        resultInfo('error', t('employerManageJobs.deactivateFailedTitle'), t('employerManageJobs.close'));
      }

      console.error('Error deactivating job:', error);
    }
  };

  return (
    <div>
      {
        parseInt(vacanciesCount) === 0
          ? <p className='mt-3 text-muted'>{t('employerManageJobs.noVacancies')}</p>
          : showVacancies
            ? (
              <button
                onClick={() => setShowVacancies(false)}
                className='showVacanciesButton'
              >
                {t('employerManageJobs.hideVacancies')}
              </button>
            ) : (
              <button
                onClick={() => handleShowVacancies()}
                className='showVacanciesButton'
              >
                {`${t('employerManageJobs.showVacancies')} - ${vacanciesCount}`}
              </button>
            )
      }
      {
        showVacancies && (
          <div>
            {
              smallLoading
                ? (
                  <div className='mt-4 text-center'>
                    <SmallLoading />
                  </div>
                ) : (
                  <div className='table-responsive'>
                    <table className='mt-2 mb-0 table'>
                      <thead>
                        <tr className='table-active TrFirst'>
                          <th className='' scope='col p-3'>
                            {t('employerManageJobs.jobs')}
                          </th>
                          <th className='' scope='col p-3' style={{ whiteSpace: 'nowrap' }}>
                            {t('employerManageJobs.vacancyID')}
                          </th>
                          <th className='' scope='col p-3' style={{ whiteSpace: 'nowrap' }}>
                            {t('employerManageJobs.location')}
                          </th>
                          <th
                            scope='col p-3'
                            onClick={() => handleSortChange('date')}
                            className={`${sort === 'date' ? 'fw-bold' : ''}`}
                            style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            {t('employerManageJobs.postedOn')} 🔻
                          </th>
                          <th className='' scope='col p-3' title={t('employerManageJobs.views')}>
                            {/* {t('employerManageJobs.views')} */}
                            <VisibilityOutlinedIcon />
                          </th>
                          <th
                            scope='col p-3'
                            onClick={() => handleSortChange('apply')}
                            className={`${sort === 'apply' ? 'fw-bold' : ''}`}
                            style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            <p>
                              {t('employerManageJobs.jobseeker')} 🔻
                            </p>
                          </th>
                          <th className='' scope='col p-3' title={t('employerManageJobs.notifiedJobseekers')}>
                            {/* {t('employerManageJobs.notifiedJobseekers')} */}
                            <MailOutlinedIcon />
                          </th>
                          <th className='' scope='col p-3'>
                            {t('employerManageJobs.status')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          vacancies.map(i => (
                            <tr className='TrDefoult' key={i.Job.id}>
                              <td className=''>
                                <Link to={`/user/managejob/accdetail/${i.Job.slug}`}>
                                  {i.Job.title}
                                </Link>
                              </td>
                              <td className=''>
                                {i.Job.id}
                              </td>
                              <td className=''>
                                {i.Job.location}
                              </td>
                              <td className=''>
                                {i.Job.created.substring(0, 10)}
                              </td>
                              <td className=''>
                                {i.Job.view_count}
                              </td>
                              <td className=''>
                                {t('employerManageJobs.total')}:{' '}
                                {i.totalCandidate}{' '}<br />
                                {t('employerManageJobs.new')}:{' '}
                                {i.newApplicationCount}
                              </td>
                              <td className=''>{i.jobAlert}</td>
                              <td className='manageJobActionButton'>
                                {
                                  i.Job.status === 1
                                    ? (
                                      <button
                                        className='btn-primary'
                                        onClick={() =>handleDeactivate(i.Job.slug)}
                                      >
                                        <Tooltip title='Deactivate'>
                                          <CheckIcon />
                                        </Tooltip>
                                      </button>
                                    ) : (
                                      <button
                                        className='btn-secondary'
                                        onClick={() => handleActivate(i.Job.slug)}
                                      >
                                        <Tooltip title='Activate'>
                                          <BlockIcon />
                                        </Tooltip>
                                      </button>
                                    )
                                }
                              </td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                  </div>
                )
            }
            <div className='manageJobPagination'>
              <p className='text-muted empPaginationData'>
                {t('employerManageJobs.jobsCount')}
                {' '}
                {pagination?.from ? pagination?.from : 0} - {pagination?.to ? pagination?.to: 0}
                {' '}
                {t('pagination.of')} {pagination?.total ? pagination?.total : 0}
              </p>
              <div className='d-flex justify-content-center empPaginationButton'>
                <button
                  className='btn navButton1 me-2'
                  disabled={smallLoading || pagination?.current_page === 1}
                  onClick={() => handlePageChange(pagination?.current_page - 1)}
                >
                  {t('pagination.Prev')}
                </button>
                <button
                  className='btn navButton1'
                  disabled={smallLoading || pagination?.to >= pagination?.total}
                  onClick={() => handlePageChange(pagination?.current_page + 1)}
                >
                  {t('pagination.Next')}
                </button>
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
};

export default ShowVacancies;
